import { useT } from '../i18n/index.ts';
import { LocaleSwitcher } from '../i18n/LocaleSwitcher.tsx';
import { CONSOLE_DOCS_URL } from '../config/endpoints.ts';
import { Wordmark } from './Wordmark.tsx';

/**
 * Page footer — DESIGN.md §6.11.
 *
 * Single row of 4 legal / support links on desktop, stacked on mobile.
 * Bottom row: locale switcher. The v3 review cut the
 * 3-column product / resources / legal block — the landing page is
 * short enough that a dedicated footer nav grid reads as padding. If
 * that changes (new sections, real policy pages) reinstate the column
 * pattern.
 *
 * Links stay as `#` anchors until the real policy / about pages land.
 * Rendering them today keeps the final layout honest so we don't
 * rebalance the footer the day the content arrives.
 */
const FOOTER_LINKS = [
  { key: 'about', labelKey: 'footer.link.about' as const, href: CONSOLE_DOCS_URL },
  { key: 'privacy', labelKey: 'footer.link.privacy' as const, href: CONSOLE_DOCS_URL },
  { key: 'terms', labelKey: 'footer.link.terms' as const, href: CONSOLE_DOCS_URL },
  { key: 'support', labelKey: 'footer.link.support' as const, href: 'mailto:support@nadoutong.org' },
] as const;

export function Footer(): JSX.Element {
  const t = useT();
  return (
    <footer data-testid="footer" className="ghost-t bg-linen">
      <div className="mx-auto max-w-7xl px-6 py-14">
        <div className="flex flex-col gap-8 md:flex-row md:items-center md:justify-between">
          <Wordmark />
          <nav
            aria-label={t('footer.nav.aria_label')}
            className="flex flex-wrap items-center gap-x-8 gap-y-3 text-[13.5px] text-ink-soft"
          >
            {FOOTER_LINKS.map((link) => (
              <a
                key={link.key}
                href={link.href}
                className="transition-colors hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-claude focus-visible:ring-offset-2 focus-visible:ring-offset-linen"
                data-testid={`footer-link-${link.key}`}
              >
                {t(link.labelKey)}
              </a>
            ))}
          </nav>
        </div>
        <div className="ghost-t mt-10 flex justify-end pt-6 font-mono text-[12px] text-ink-subtle">
          <LocaleSwitcher />
        </div>
      </div>
    </footer>
  );
}
